import React, { useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { SubmitErrorHandler } from 'react-hook-form'
import { Box, TextField, Typography } from '@mui/material'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { FieldActivity, FieldActivitySelect, Frequency, FrequencySelect } from '#/features/register-business/lib/constants-step-information'
import { getStepperInputStyles } from '#/features/register-business/ui/shared/form-styles'
import { IEmailForms } from '#/features/register-by-email/model/types'
import { ButtonUI } from '#/shared'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import { SelectUI } from '#/shared/ui/select'

import { setFieldActivity, setFrequency, setNumberStuff, switchNextStep } from '../../model/stepper-slice'

import styles from './step-information.module.scss'

const getSelectStyles = (hasError: boolean) => ({
	boxShadow: 'none',
	borderRadius: '13px',
	backgroundColor: '#4B4B4B',
	border: hasError ? `2px solid #ff2372` : '2px solid #40404E',
	color: '#A6A5A5',
	'.MuiSelect-icon': {
		color: '#A6A5A5',
	},
	'.MuiOutlinedInput-notchedOutline': {
		border: 0,
	},
	'&.Mui-focused': {
		borderColor: '#4B4BFF',
	},
})

export const StepInformation = () => {
	const dispatch = useAppDispatch()
	const { frequency, fieldActivity, numberStuff } = useAppSelector((state) => state.stepper.dataForCreate)
	const { showMessage } = useShowDataStore()
	const router = useRouter()

	const handleCancel = () => {
	  router.back()
	}

	const methods = useForm({
		mode: 'onSubmit',
		defaultValues: {
			frequency,
			fieldActivity,
			numberStuff,
		},
	})

	const {
		control,
		handleSubmit,
		formState: { errors },
		register,
	} = methods

	const selectOptions = useMemo(
		() => ({
			fieldActivity: Object.values(FieldActivitySelect),
			frequency: Object.values(FrequencySelect),
		}),
		[]
	)

	const onSubmit = (data: any) => {
		dispatch(setNumberStuff(data.numberStuff))
		dispatch(switchNextStep())
	}

	const handleError: SubmitErrorHandler<IEmailForms> = (formErrors) => {
		showMessage(Object.values(formErrors)[0].message || 'Неверные данные')
	}

	return (
		<FormProvider {...methods}>
			<form id='step1' className={styles.form} onSubmit={handleSubmit(onSubmit, handleError)}>
				<Box className={styles.formInner}>
					<Box className={styles.element}>
						<Typography>Ваша сфера деятельности</Typography>
						<Controller
							name='fieldActivity'
							control={control}
							rules={{ required: 'Выберите сферу деятельности' }}
							render={({ field }) => (
								<SelectUI
									{...field}
									value={field.value ?? ''}
									onChange={(event: any) => {
										field.onChange(event.target.value)
										dispatch(setFieldActivity(event.target.value as FieldActivity))
									}}
									list={selectOptions.fieldActivity}
									sx={getSelectStyles(!!errors.fieldActivity)}
								/>
							)}
						/>
						{errors.fieldActivity && <Typography className={styles.error}>{errors.fieldActivity.message}</Typography>}
					</Box>

					<Box className={styles.element}>
						<Typography>Как часто планируете использовать нейросети?</Typography>
						<Controller
							name='frequency'
							control={control}
							rules={{ required: 'Выберите частоту использования' }}
							render={({ field }) => (
								<SelectUI
									{...field}
									value={field.value ?? ''}
									onChange={(event: any) => {
										field.onChange(event.target.value)
										dispatch(setFrequency(event.target.value as Frequency))
									}}
									list={selectOptions.frequency}
									sx={getSelectStyles(!!errors.frequency)}
								/>
							)}
						/>
						{errors.frequency && <Typography className={styles.error}>{errors.frequency.message}</Typography>}
					</Box>

					<Box className={styles.element}>
						<Typography>Сколько вы планируете подключить сотрудников?</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.numberStuff)}
							error={!!errors.numberStuff}
							helperText={errors.numberStuff?.message}
							FormHelperTextProps={{ className: styles.helperText }}
							{...register('numberStuff', {
								required: 'Укажите количество сотрудников',
								pattern: {
									value: /^[0-9]+$/,
									message: 'Введите количество числом',
								},
							})}
						/>
					</Box>

					<Box className={styles.btn} display='flex' justifyContent='center'>
						<Box paddingRight={1}>
							<ButtonUI onClick={handleCancel} text='Отмена' />
						</Box>
						<Box>
							<ButtonUI onClick={handleSubmit(onSubmit, handleError)} text='Продолжить' />
						</Box>
					</Box>
				</Box>
			</form>
		</FormProvider>
	)
}
